import type { ParsedQs } from 'qs';
import { parse, stringify } from 'qs';

/**
 * 쿼리 객체를 파싱하여 key-value 쌍으로 변환.
 * 쿼리스트링 값이 string[] 타입일 경우 마지막 값을 반환
 * string | string[] | undefined 타입의 쿼리를 string | undefined 타입으로 변환
 * 빈 문자열('')은 제외하고 판단
 * @param query - 쿼리 객체
 * @param shouldDecode - url decode 여부
 * @returns 파싱된 결과 객체
 */
export const parseQueryObject = (query: ParsedQs, shouldDecode = false): Record<string, string | undefined> => {
  const result: Record<string, string | undefined> = {};

  for (const key in query) {
    if (Object.prototype.hasOwnProperty.call(query, key)) {
      result[key] = parseQueryToString(query[key], shouldDecode);
    }
  }

  return result;
};

/**
 * 쿼리스트링을 파싱하여 문자열(string)로 변환
 * string[] 타입의 쿼리스트링 값이 있을 경우 마지막 값을 반환
 * 빈 문자열('')은 제외하고 판단
 * 파싱 과정에서 value 는 url decode 처리
 * @param queryKey - 쿼리스트링 키
 * @param queryValue - 쿼리스트링 값
 * @param shouldDecode - url decode 여부
 * @returns 문자열로 변환된 쿼리스트링 값
 */
export const parseQueryToString = (queryValue: ParsedQs[number], shouldDecode = false): string | undefined => {
  try {
    return parse(
      stringify(
        { queryKey: queryValue },
        {
          arrayFormat: 'repeat',
          filter: (_, val) => (val ? val : undefined), // 빈 문자열('') 제거
        },
      ),
      {
        ignoreQueryPrefix: true,
        duplicates: 'last',
        decoder: (str, defaultDecoder, charset, type) => {
          if (shouldDecode && type === 'value') {
            return decodeURIComponent(decodeURIComponent(str));
          }
          return defaultDecoder(str, defaultDecoder, charset);
        },
      },
    ).queryKey as string | undefined;
  } catch (error) {
    return undefined;
  }
};
